import React, { useState, useEffect, useRef } from 'react';

const InteractiveWeatherPlayer = ({ 
  weatherData, 
  timeline,
  audioUrl,
  onClose
}) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [segments, setSegments] = useState([]);
  const [activeSegmentIndex, setActiveSegmentIndex] = useState(0);
  const [contentKey, setContentKey] = useState(0);

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 50);
  }, []);

  // Build segments from timeline
  useEffect(() => {
    if (!timeline || timeline.length === 0) return;

    const segs = [];
    
    timeline.forEach((item) => {
      if (item.display) {
        segs.push({
          id: segs.length,
          type: item.type,
          startTime: item.startTime,
          endTime: item.endTime,
          data: item.display
        });
      }
    });

    setSegments(segs);
  }, [timeline]);

  const currentDisplay = segments[activeSegmentIndex]?.data || {
    type: 'intro',
    location: weatherData.location,
    weather: weatherData.weather,
    precipChance: 0,
    date: 'now',
    dayName: 'Current'
  };

  const getBackgroundGradient = (condition) => {
    const lower = condition?.toLowerCase() || '';
    const temp = currentDisplay.weather?.temperature || 70;
    
    if (lower.includes('clear') || lower.includes('sunny')) {
      if (temp > 85) return 'linear-gradient(135deg, #fff7ed 0%, #fed7aa 30%, #fb923c 60%, #f97316 100%)';
      return 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 30%, #fcd34d 70%, #f59e0b 100%)';
    } else if (lower.includes('cloud')) {
      return 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 30%, #bae6fd 70%, #7dd3fc 100%)';
    } else if (lower.includes('rain') || lower.includes('drizzle')) {
      return 'linear-gradient(135deg, #dbeafe 0%, #93c5fd 30%, #60a5fa 70%, #3b82f6 100%)';
    } else if (lower.includes('snow')) {
      return 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 30%, #e2e8f0 70%, #cbd5e1 100%)';
    } else if (lower.includes('storm') || lower.includes('thunder')) {
      return 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 30%, #a5b4fc 70%, #818cf8 100%)';
    }
    return 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 30%, #fcd34d 70%, #f59e0b 100%)';
  };

  const getWeatherParticles = () => {
    if (currentDisplay.type !== 'weather') return null;
    const condition = currentDisplay.weather?.condition?.toLowerCase() || '';
    if (condition.includes('rain') || condition.includes('drizzle')) return 'rain';
    if (condition.includes('snow')) return 'snow';
    return null;
  };

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(err => console.error('Play error:', err));
    }
  };

  const skipBackward = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = Math.max(0, audio.currentTime - 10);
    }
  };

  const skipForward = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = Math.min(duration, audio.currentTime + 10);
    }
  };

  const handleProgressClick = (e) => {
    const audio = audioRef.current;
    if (!audio || duration === 0) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    audio.currentTime = newTime;
  };

  const formatTime = (time) => {
    if (isNaN(time) || time === 0) return "0:00";
    const m = Math.floor(time / 60);
    const s = Math.floor(time % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose?.(), 300);
  };

  // Manual segment navigation
  const goToNextSegment = () => {
    if (activeSegmentIndex < segments.length - 1) {
      setActiveSegmentIndex(prev => prev + 1);
      setContentKey(prev => prev + 1);
    }
  };

  const goToPrevSegment = () => {
    if (activeSegmentIndex > 0) {
      setActiveSegmentIndex(prev => prev - 1);
      setContentKey(prev => prev + 1);
    }
  };

  const goToSegment = (index) => {
    setActiveSegmentIndex(index);
    setContentKey(prev => prev + 1);
  };

  const getSegmentTitle = (type) => {
    const titles = {
      'intro': 'Introduction',
      'current': 'Current',
      'hourly': 'Next Hours',
      'hourly_later': 'Later Today',
      'nearby': 'Nearby',
      'daily': 'Weekly',
      'outro': 'Closing'
    };
    return titles[type] || 'Update';
  };

  const getSegmentIcon = (type) => {
    const icons = {
      'intro': '🌤️',
      'current': '📍',
      'hourly': '⏰',
      'hourly_later': '🕐',
      'nearby': '🗺️',
      'daily': '📅',
      'outro': '👋'
    };
    return icons[type] || '🌡️';
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => {
      setIsPlaying(false);
      setTimeout(handleClose, 400);
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", updateDuration);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", updateDuration);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.pause();
      audio.currentTime = 0;
    };
  }, [duration]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio && audioUrl) {
      audio.play()
        .then(() => setIsPlaying(true))
        .catch((error) => console.error('Autoplay failed:', error));
    }
    
    return () => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    };
  }, [audioUrl]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const weatherParticles = getWeatherParticles();

  return (
    <div
      style={{
        marginTop: '12px',
        background: getBackgroundGradient(currentDisplay.weather?.condition || 'clear'),
        borderRadius: '18px',
        padding: '12px',
        width: '300px',
        boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'scale(1)' : 'scale(0.92)',
        transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        overflow: 'hidden',
        position: 'relative',
        backdropFilter: 'blur(10px)'
      }}
    >
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      {/* Weather Particles */}
      {weatherParticles === 'rain' && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', overflow: 'hidden', borderRadius: '18px', zIndex: 1 }}>
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: `${Math.random() * 100}%`,
                top: '-10px',
                width: '2px',
                height: '15px',
                background: 'linear-gradient(to bottom, rgba(255,255,255,0.6), rgba(255,255,255,0.1))',
                animation: `fall ${1 + Math.random() * 1}s linear infinite`,
                animationDelay: `${Math.random() * 2}s`,
                opacity: 0.6
              }}
            />
          ))}
        </div>
      )}

      {weatherParticles === 'snow' && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', overflow: 'hidden', borderRadius: '18px', zIndex: 1 }}>
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: `${Math.random() * 100}%`,
                top: '-10px',
                width: '6px',
                height: '6px',
                background: 'rgba(255,255,255,0.9)',
                borderRadius: '50%',
                animation: `snowfall ${3 + Math.random() * 2}s linear infinite`,
                animationDelay: `${Math.random() * 3}s`,
                boxShadow: '0 0 4px rgba(255,255,255,0.5)'
              }}
            />
          ))}
        </div>
      )}

      {/* Ambient light */}
      <div
        style={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          right: '-50%',
          bottom: '-50%',
          background: 'radial-gradient(circle at 50% 0%, rgba(255,255,255,0.15), transparent 70%)',
          pointerEvents: 'none',
          animation: 'ambientPulse 4s ease-in-out infinite',
          zIndex: 0
        }}
      />

      {/* Header */}
      <div
        style={{
          padding: '10px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(255, 255, 255, 1)',
          backdropFilter: 'blur(20px)',
          borderRadius: '10px',
          border: '1px solid rgba(107, 107, 107, 0.25)',
          position: 'relative',
          zIndex: 2
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            style={{
              width: '6px',
              height: '6px',
              background: '#ff2323ff',
              borderRadius: '50%',
              animation: 'pulse 2s infinite',
              boxShadow: '0 0 8px rgba(239, 68, 68, 0.8)'
            }}
          />
          <span
            style={{
              fontSize: '10px',
              fontWeight: '700',
              color: '#111827',
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
            }}
          >
            Live
          </span>
          <div style={{
            fontSize: '8px',
            padding: '2px 5px',
            background: 'rgba(0,0,0,0.1)',
            borderRadius: '4px',
            color: '#374151',
            fontWeight: '600'
          }}>
            {formatTime(currentTime)}
          </div>
        </div>

        <button
          onClick={handleClose}
          style={{
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
            background: 'rgba(255, 255, 255, 0.5)',
            backdropFilter: 'blur(10px)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.9)';
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.5)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <svg width="14" height="14" viewBox="0 0 15 15" fill="none">
            <path
              d="M11.7816 4.03157C12.0062 3.80702 12.0062 3.44295 11.7816 3.2184C11.5571 2.99385 11.193 2.99385 10.9685 3.2184L7.50005 6.68682L4.03164 3.2184C3.80708 2.99385 3.44301 2.99385 3.21846 3.2184C2.99391 3.44295 2.99391 3.80702 3.21846 4.03157L6.68688 7.49999L3.21846 10.9684C2.99391 11.193 2.99391 11.557 3.21846 11.7816C3.44301 12.0061 3.80708 12.0061 4.03164 11.7816L7.50005 8.31316L10.9685 11.7816C11.193 12.0061 11.5571 12.0061 11.7816 11.7816C12.0062 11.557 12.0062 11.193 11.7816 10.9684L8.31322 7.49999L11.7816 4.03157Z"
              fill="black"
              fillRule="evenodd"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      {/* Display Card */}
      <div
        style={{
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(30px)',
          borderRadius: '14px',
          padding: '16px',
          boxShadow: '0 6px 24px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)',
          position: 'relative',
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.5)',
          height: '200px',
          width: '276px',
          zIndex: 2
        }}
      >
        {/* Navigation Arrows */}
        {segments.length > 1 && (
          <>
            <button
              onClick={goToPrevSegment}
              disabled={activeSegmentIndex === 0}
              style={{
                position: 'absolute',
                left: '8px',
                top: '90%',
                transform: 'translateY(-50%)',
                width: '28px',
                height: '28px',
                background: activeSegmentIndex === 0 ? 'rgba(0,0,0,0.1)' : '#cee5fff0',
                border: 'none',
                borderRadius: '50%',
                cursor: activeSegmentIndex === 0 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                zIndex: 10,
                opacity: activeSegmentIndex === 0 ? 0.3 : 1,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (activeSegmentIndex !== 0) {
                  e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
              }}
            >
              <svg width="12" height="12" viewBox="0 0 15 15" fill="none">
                <path d="M8.84182 3.13514C9.04327 3.32401 9.05348 3.64042 8.86462 3.84188L5.43521 7.49991L8.86462 11.1579C9.05348 11.3594 9.04327 11.6758 8.84182 11.8647C8.64036 12.0535 8.32394 12.0433 8.13508 11.8419L4.38508 7.84188C4.20477 7.64955 4.20477 7.35027 4.38508 7.15794L8.13508 3.15794C8.32394 2.95648 8.64036 2.94628 8.84182 3.13514Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd" />
              </svg>
            </button>

            <button
              onClick={goToNextSegment}
              disabled={activeSegmentIndex === segments.length - 1}
              style={{
                position: 'absolute',
                right: '8px',
                top: '90%',
                transform: 'translateY(-50%)',
                width: '28px',
                height: '28px',
                background: activeSegmentIndex === segments.length - 1 ? 'hsla(0, 0%, 0%, 0.10)' : '#cee5fff0',
                border: 'none',
                borderRadius: '50%',
                cursor: activeSegmentIndex === segments.length - 1 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                zIndex: 10,
                opacity: activeSegmentIndex === segments.length - 1 ? 0.3 : 1,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (activeSegmentIndex !== segments.length - 1) {
                  e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
              }}
            >
              <svg width="12" height="12" viewBox="0 0 15 15" fill="none">
                <path d="M6.1584 3.13508C6.35985 2.94621 6.67627 2.95642 6.86514 3.15788L10.6151 7.15788C10.7954 7.3502 10.7954 7.64949 10.6151 7.84182L6.86514 11.8418C6.67627 12.0433 6.35985 12.0535 6.1584 11.8646C5.95694 11.6757 5.94673 11.3593 6.1356 11.1579L9.565 7.49985L6.1356 3.84182C5.94673 3.64036 5.95694 3.32394 6.1584 3.13508Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd" />
              </svg>
            </button>
          </>
        )}

        {/* Intro Card */}
        {currentDisplay.type === 'intro' && (
          <div key={contentKey} style={{ 
            animation: 'contentFadeIn 0.4s ease',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{ fontSize: '56px', marginBottom: '12px', animation: 'float 3s ease-in-out infinite' }}>
              🌤️
            </div>
            <div style={{ fontSize: '18px', fontWeight: '800', color: '#111827', marginBottom: '6px' }}>
              Weather Forecast
            </div>
            <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>
              {weatherData.location.city}, {weatherData.location.state}
            </div>
          </div>
        )}

        {/* Outro Card */}
        {currentDisplay.type === 'outro' && (
          <div key={contentKey} style={{ 
            animation: 'contentFadeIn 0.4s ease',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{ fontSize: '56px', marginBottom: '12px', animation: 'float 3s ease-in-out infinite' }}>
              ✨
            </div>
            <div style={{ fontSize: '18px', fontWeight: '800', color: '#111827', marginBottom: '6px' }}>
              Stay Safe!
            </div>
            <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>
              Thanks for listening
            </div>
          </div>
        )}

        {/* Weather Card */}
        {currentDisplay.type === 'weather' && (
          <div key={contentKey} style={{ 
            animation: 'contentFadeIn 0.4s ease',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Top Section */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-start',
              marginBottom: '12px'
            }}>
              <div>
                <div style={{
                  fontSize: '11px',
                  fontWeight: '700',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '2px'
                }}>
                 {currentDisplay.location.city}
                </div>
                <div style={{
                  display: 'inline-block',
                  padding: '3px 8px',
                  background: currentDisplay.dayName === 'Current' || currentDisplay.dayName === 'Now'
                    ? 'linear-gradient(135deg, #5697ffff, #2563eb)' 
                    : 'linear-gradient(135deg, #ff995eff, #ff6224ff)',
                  color: 'white',
                  borderRadius: '6px',
                  fontSize: '9px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px'
                }}>
                  {currentDisplay.dayName}
                </div>
              </div>
            </div>

            {/* Middle Section */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px',
              flex: 1
            }}>
              <div>
                <div style={{ 
                  fontSize: '52px', 
                  fontWeight: '500', 
                  color: '#111827', 
                  lineHeight: 0.9,
                  marginBottom: '4px'
                }}>
                  {currentDisplay.weather.temperature}°
                </div>
                <div style={{ 
                  fontSize: '11px', 
                  color: '#6b7280', 
                  fontWeight: '600'
                }}>
                  Feels {currentDisplay.weather.feelsLike}°
                </div>
                <div style={{ 
                  fontSize: '13px', 
                  fontWeight: '500', 
                  color: '#374151',
                  marginTop: '6px'
                }}>
                  {currentDisplay.weather.condition}
                </div>
              </div>
              <div style={{ 
                fontSize: '64px', 
                lineHeight: 1,
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))',
                animation: 'float 3s ease-in-out infinite'
              }}>
                {currentDisplay.weather.icon}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Player Controls */}
      <div
        style={{
          background: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(20px)',
          borderRadius: '14px',
          padding: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          boxShadow: '0 3px 12px rgba(0,0,0,0.06)',
          border: '1px solid rgba(255,255,255,0.5)',
          position: 'relative',
          zIndex: 2
        }}
      >
        {/* Progress Bar */}
        <div 
          onClick={handleProgressClick}
          style={{
            width: '100%',
            height: '5px',
            background: 'rgba(0,0,0,0.08)',
            borderRadius: '6px',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <div style={{
            height: '100%',
            width: `${progress}%`,
            background: 'linear-gradient(90deg, #3b82f6, #2563eb)',
            borderRadius: '6px',
            transition: 'width 0.1s linear',
            boxShadow: '0 0 6px rgba(59, 130, 246, 0.5)',
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: '10px',
              height: '10px',
              background: 'white',
              borderRadius: '50%',
              boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
              border: '2px solid #3b82f6'
            }} />
          </div>
        </div>

        {/* Time Display */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '8px',
          color: '#6b7280',
          fontWeight: '600'
        }}>
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>

        {/* Controls */}
        <div style={{
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <button
            onClick={skipBackward}
            style={{
              width: '32px',
              height: '32px',
              background: 'linear-gradient(135deg, #f3f4f6, #e5e7eb)',
              border: 'none',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#374151',
              transition: 'all 0.2s ease',
              fontSize: '9px',
              fontWeight: '700',
              boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #e5e7eb, #d1d5db)';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #f3f4f6, #e5e7eb)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            -10s
          </button>

          <button
            onClick={togglePlayPause}
            style={{
              width: '44px',
              height: '44px',
              background: 'linear-gradient(135deg, #111827, #1f2937)',
              border: 'none',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
              position: 'relative'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.25)';
            }}
          >
            {isPlaying ? (
              <svg width="16" height="16" viewBox="0 0 15 15" fill="none">
                <path d="M6.04995 2.74998C6.04995 2.44623 5.80371 2.19998 5.49995 2.19998C5.19619 2.19998 4.94995 2.44623 4.94995 2.74998V12.25C4.94995 12.5537 5.19619 12.8 5.49995 12.8C5.80371 12.8 6.04995 12.5537 6.04995 12.25V2.74998ZM10.05 2.74998C10.05 2.44623 9.80371 2.19998 9.49995 2.19998C9.19619 2.19998 8.94995 2.44623 8.94995 2.74998V12.25C8.94995 12.5537 9.19619 12.8 9.49995 12.8C9.80371 12.8 10.05 12.5537 10.05 12.25V2.74998Z" fill="currentColor" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 15 15" fill="none" style={{ marginLeft: '2px' }}>
                <path d="M3.24182 2.32181C3.3919 2.23132 3.5784 2.22601 3.73338 2.30781L12.7334 7.05781C12.8974 7.14436 13 7.31457 13 7.5C13 7.68543 12.8974 7.85564 12.7334 7.94219L3.73338 12.6922C3.5784 12.774 3.3919 12.7687 3.24182 12.6782C3.09175 12.5877 3 12.4252 3 12.25V2.75C3 2.57476 3.09175 2.4123 3.24182 2.32181Z" fill="currentColor" />
              </svg>
            )}
            {isPlaying && (
              <div style={{
                position: 'absolute',
                inset: '-3px',
                border: '2px solid rgba(59, 130, 246, 0.5)',
                borderRadius: '50%',
                animation: 'ripple 1.5s ease-out infinite'
              }} />
            )}
          </button>

          <button
            onClick={skipForward}
            style={{
              width: '32px',
              height: '32px',
              background: 'linear-gradient(135deg, #f3f4f6, #e5e7eb)',
              border: 'none',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#374151',
              transition: 'all 0.2s ease',
              fontSize: '9px',
              fontWeight: '700',
              boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #e5e7eb, #d1d5db)';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #f3f4f6, #e5e7eb)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            +10s
          </button>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.1); }
        }
        
        @keyframes fall {
          to { transform: translateY(350px); opacity: 0; }
        }
        
        @keyframes snowfall {
          to { transform: translateY(350px) translateX(40px); opacity: 0; }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        
        @keyframes ambientPulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.5; }
        }
        
        @keyframes ripple {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(1.3); opacity: 0; }
        }
        
        @keyframes contentFadeIn {
          0% { opacity: 0; transform: scale(0.96); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default InteractiveWeatherPlayer;